import { useCallback, useEffect, useRef, type RefObject } from "react";
import { ContextChangedError, WalletOperation } from "../wallet/wallet-operation";
import type { WalletSessionController } from "../wallet/wallet-session";

type WalletOperationsOptions = {
  walletController: WalletSessionController;
  activeContext: RefObject<{ scopeKey: string }>;
  scopeKey: string;
};

export function useWalletOperations({ walletController, activeContext, scopeKey }: WalletOperationsOptions) {
  const operations = useRef(new Set<WalletOperation>());
  const beginOperation = useCallback(() => {
    if (activeContext.current.scopeKey !== scopeKey) throw new ContextChangedError();
    const provider = walletController.getProvider();
    if (!provider) throw new Error("Connect a wallet first");
    const operation = new WalletOperation(walletController, provider, () => activeContext.current.scopeKey, scopeKey);
    operations.current.add(operation);
    return operation;
  }, [scopeKey, walletController, activeContext]);
  const endOperation = useCallback((operation: WalletOperation) => {
    operation.dispose();
    operations.current.delete(operation);
  }, []);
  useEffect(() => {
    for (const operation of operations.current) {
      try { operation.assertCurrent(); } catch { /* Context changes abort old operations. */ }
    }
  }, [scopeKey]);
  useEffect(() => () => {
    for (const operation of operations.current) { operation.cancel(); operation.dispose(); }
    operations.current.clear();
  }, []);

  const cancelOperations = useCallback(() => {
    for (const operation of operations.current) operation.cancel();
  }, []);

  return { beginOperation, endOperation, cancelOperations };
}
